import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { ApiError } from './apiClient';

/**
 * GlobalErrorBanner — subscribes to the TanStack Query cache and surfaces the
 * most recent `ApiError` as a dismissible banner. This is the minimal toast
 * replacement the UI has until a dedicated notification lib lands.
 *
 * The banner only reacts to `ApiError`s thrown by the shared `apiFetch`
 * helper; other thrown values (e.g. validation errors raised inside a
 * mutation's `onSuccess`) are ignored so the chrome does not flash during
 * expected happy-path flows.
 */
export default function GlobalErrorBanner() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    const cache = queryClient.getQueryCache();
    const unsubscribe = cache.subscribe((event) => {
      if (event.type === 'updated' && event.query.state.status === 'error') {
        const err = event.query.state.error;
        if (err instanceof ApiError) setError(err);
      }
    });
    return unsubscribe;
  }, [queryClient]);

  if (!error) return null;

  return (
    <div
      role="alert"
      data-testid="global-error-banner"
      style={{
        position: 'fixed',
        top: 12,
        right: 12,
        zIndex: 1000,
        maxWidth: 420,
        padding: '10px 14px',
        border: '1px solid var(--slate-200, #e2e8f0)',
        background: '#fff',
        borderLeft: '3px solid var(--fail, #b91c1c)',
        boxShadow: '0 6px 20px rgba(0,0,0,0.08)',
        fontSize: 12,
      }}
    >
      <div className="eyebrow" style={{ color: 'var(--fail, #b91c1c)', marginBottom: 4 }}>
        § API ERROR · {error.status}
      </div>
      <div className="mono" style={{ wordBreak: 'break-word' }}>{error.message}</div>
      <button
        type="button"
        onClick={() => setError(null)}
        style={{
          marginTop: 8,
          fontSize: 11,
          background: 'transparent',
          border: '1px solid var(--slate-200, #e2e8f0)',
          padding: '4px 8px',
          cursor: 'pointer',
        }}
      >
        Dismiss
      </button>
    </div>
  );
}
